"use client";

import Link from "next/link";
import { useState } from "react";
import {
  Radio, FileText, Users, Truck, ShieldCheck, BarChart3,
  Calendar, ChevronRight, Check, Menu, X, Zap, Activity,
  MapPin, ClipboardList, Ambulance, Siren,
  Lock, Database, Globe, Server, Eye,
  Cpu, WifiOff, ArrowRight, Building2,
  HeartPulse, Shield,
  Terminal, Clock, UserCheck,
  Compass, Heart,
  Mail, CheckCircle2,
  Network, Gauge, Target,
  Flame, Search, AlertOctagon, Layers, Command,
  GitBranch, LayoutDashboard, Fingerprint, ChevronDown,
  ExternalLink, Star, Workflow,
} from "lucide-react";
import { cn } from "@/lib/utils";

// ─── Constants ───────────────────────────────────────────────────────────────

const PLATFORM_LINKS = [
  { label: "Command & Control", href: "/platform/dispatch" },
  { label: "Clinical Operations", href: "/platform/clinical" },
  { label: "Workforce Management", href: "/platform/workforce" },
  { label: "Governance & Compliance", href: "/platform/compliance" },
  { label: "Fleet & Assets", href: "/platform/fleet" },
  { label: "Operational Intelligence", href: "/platform/intelligence" },
];

const SOLUTION_LINKS = [
  { label: "Event Medical Operations", href: "#solutions" },
  { label: "Fire & Rescue", href: "#solutions" },
  { label: "Search & Rescue", href: "#solutions" },
  { label: "Security Operations", href: "#solutions" },
  { label: "Industrial Response", href: "#solutions" },
  { label: "Emergency Preparedness", href: "#solutions" },
];

const TRUST_STATS = [
  { value: "38", label: "Professional Roles" },
  { value: "7-Layer", label: "Permission Model" },
  { value: "99.9%", label: "Uptime SLA" },
  { value: "UK", label: "Data Hosting" },
];

const ECOSYSTEM_MODULES = [
  { icon: Radio, color: "text-blue-400", bg: "bg-blue-500/10 border-blue-500/20", label: "Command & Control", desc: "CAD dispatch, unit tracking, major incident, crew status" },
  { icon: HeartPulse, color: "text-red-400", bg: "bg-red-500/10 border-red-500/20", label: "Clinical Operations", desc: "Digital PRFs, NEWS2, offline capability, clinical audit" },
  { icon: Users, color: "text-emerald-400", bg: "bg-emerald-500/10 border-emerald-500/20", label: "Workforce Management", desc: "Shifts, HR, training hub, appraisals, payroll" },
  { icon: ShieldCheck, color: "text-purple-400", bg: "bg-purple-500/10 border-purple-500/20", label: "Governance & Compliance", desc: "CQC, incidents, risk register, audit trails" },
  { icon: Truck, color: "text-amber-400", bg: "bg-amber-500/10 border-amber-500/20", label: "Fleet & Assets", desc: "Inspections, defects, servicing, gas tracking" },
  { icon: BarChart3, color: "text-cyan-400", bg: "bg-cyan-500/10 border-cyan-500/20", label: "Operational Intelligence", desc: "KPI dashboards, readiness scoring, analytics" },
  { icon: FileText, color: "text-indigo-400", bg: "bg-indigo-500/10 border-indigo-500/20", label: "Documentation Hub", desc: "Policy register, PGDs, version control, read receipts" },
  { icon: Calendar, color: "text-violet-400", bg: "bg-violet-500/10 border-violet-500/20", label: "Event Operations", desc: "Operational readiness system — Medical Event Plans, command structure, staffing, risk matrix, hospital pathways, client portal, and post-event analytics" },
];

const SECTORS = [
  {
    icon: HeartPulse,
    color: "text-blue-400",
    bg: "bg-blue-500/10 border-blue-500/20",
    glow: "group-hover:shadow-blue-500/10",
    label: "Event Medical Operations",
    desc: "Multi-site medical coordination, CAD dispatch, clinical documentation, crew management, and client reporting — built for high-attendance events.",
    tags: ["CAD Dispatch", "Digital PRFs", "Crew Rostering", "Client Reports"],
  },
  {
    icon: Siren,
    color: "text-red-400",
    bg: "bg-red-500/10 border-red-500/20",
    glow: "group-hover:shadow-red-500/10",
    label: "Ambulance & Clinical Services",
    desc: "Complete ambulance service platform — from CQC compliance and clinical audit to fleet management and workforce administration.",
    tags: ["CQC Compliance", "Clinical Audit", "Fleet Management", "NHS Standards"],
  },
  {
    icon: Flame,
    color: "text-orange-400",
    bg: "bg-orange-500/10 border-orange-500/20",
    glow: "group-hover:shadow-orange-500/10",
    label: "Fire & Rescue",
    desc: "Fire appliance management, mobilisation coordination, resource tracking, operational readiness, and multi-agency incident command.",
    tags: ["Appliance Management", "Mobilisation", "Resource Tracking", "Joint Agency"],
  },
  {
    icon: Compass,
    color: "text-cyan-400",
    bg: "bg-cyan-500/10 border-cyan-500/20",
    glow: "group-hover:shadow-cyan-500/10",
    label: "Search & Rescue",
    desc: "Search planning, GIS mapping, sector management, team deployment, SAR equipment, and operational log for lowland, mountain, and water rescue.",
    tags: ["Search Planning", "GIS Mapping", "SAR Equipment", "Deployment Log"],
  },
  {
    icon: Shield,
    color: "text-yellow-400",
    bg: "bg-yellow-500/10 border-yellow-500/20",
    glow: "group-hover:shadow-yellow-500/10",
    label: "Security Operations",
    desc: "Patrol management, security incident reporting, access control, missing persons coordination, and welfare officer oversight.",
    tags: ["Patrol Tracker", "Incident Log", "Access Control", "Missing Persons"],
  },
  {
    icon: Building2,
    color: "text-violet-400",
    bg: "bg-violet-500/10 border-violet-500/20",
    glow: "group-hover:shadow-violet-500/10",
    label: "Industrial Response",
    desc: "Onsite emergency response teams — responder availability, equipment status, incident history, and audit readiness for industrial and construction environments.",
    tags: ["Responder Tracking", "Equipment Status", "Audit Ready", "Incident Log"],
  },
  {
    icon: GitBranch,
    color: "text-emerald-400",
    bg: "bg-emerald-500/10 border-emerald-500/20",
    glow: "group-hover:shadow-emerald-500/10",
    label: "Joint Operations & Multi-Agency",
    desc: "Multi-agency coordination across clinical, fire, SAR, and security disciplines — shared command structures, cross-agency resource visibility, unified incident records, and multi-service governance.",
    tags: ["Multi-Agency Command", "Shared Incidents", "Cross-Service Resources", "Unified Governance"],
  },
];

const BRAND_PILLARS = [
  {
    icon: Target,
    label: "Frontline Experience, Embedded",
    desc: "Designed by and alongside operational professionals — paramedics, incident commanders, fire OICs, and control room staff. Every screen reflects how real operations actually work.",
    color: "text-blue-400",
    bg: "bg-blue-500/10 border-blue-500/20",
  },
  {
    icon: ShieldCheck,
    label: "Compliance First",
    desc: "Governance, audit trails, and regulatory compliance are architecturally embedded — not added as afterthoughts.",
    color: "text-purple-400",
    bg: "bg-purple-500/10 border-purple-500/20",
  },
  {
    icon: GitBranch,
    label: "Multi-Agency Ready",
    desc: "Built to operate across clinical, fire, SAR, security, and industrial disciplines within a single unified platform.",
    color: "text-cyan-400",
    bg: "bg-cyan-500/10 border-cyan-500/20",
  },
  {
    icon: WifiOff,
    label: "Resilient By Design",
    desc: "Offline-first architecture, redundant infrastructure, and field-ready interfaces that perform when connectivity fails.",
    color: "text-amber-400",
    bg: "bg-amber-500/10 border-amber-500/20",
  },
  {
    icon: Network,
    label: "Connected Ecosystem",
    desc: "Every module shares the same data layer. Actions in dispatch flow through to clinical, governance, and intelligence automatically.",
    color: "text-emerald-400",
    bg: "bg-emerald-500/10 border-emerald-500/20",
  },
  {
    icon: Layers,
    label: "Built For Scale",
    desc: "From single-site services to multi-agency enterprise deployments. One platform grows with your operational scope.",
    color: "text-indigo-400",
    bg: "bg-indigo-500/10 border-indigo-500/20",
  },
];

const OPERATIONAL_ENV = [
  {
    icon: Eye,
    label: "Low-Light Environments",
    desc: "High-contrast interfaces, large touch targets, and minimal cognitive load — designed for use in darkness, adverse weather, and high-stress conditions.",
  },
  {
    icon: Radio,
    label: "Incident Pressure",
    desc: "Workflows are structured around operational tempo. Critical actions are reachable in minimal steps. There is no room for interface friction when a patient is deteriorating.",
  },
  {
    icon: WifiOff,
    label: "Offline Operations",
    desc: "PRFs, incident records, briefings, and status updates continue without connectivity. Data queues locally and syncs automatically when reconnected — with full conflict resolution.",
  },
  {
    icon: GitBranch,
    label: "Multi-Agency Coordination",
    desc: "Bronze, silver, and gold command structures. Cross-service resource visibility. Shared incident logs. OpsCom is architecturally designed for multi-agency command environments.",
  },
  {
    icon: FileText,
    label: "Compliance Burden",
    desc: "CQC, NHS data standards, ICO obligations, and clinical governance don't pause during an incident. OpsCom makes compliance the default state — not an extra task after the shift.",
  },
  {
    icon: Cpu,
    label: "Radio Traffic & Communications",
    desc: "Integrated radio logging, message threading, and communications audit. Every broadcast, every channel, every decision — captured and attributable.",
  },
];

const GOVERNANCE_FEATURES = [
  {
    icon: Fingerprint,
    label: "Complete Audit Trails",
    desc: "Every data entry, every update, every deletion is logged with the user identity, timestamp, and IP address. Immutable. Uneditable. Always available.",
  },
  {
    icon: FileText,
    label: "CQC Inspection Readiness",
    desc: "CQC workflows, quality statements, evidence tracking, action plans, and inspection simulation built directly into the platform.",
  },
  {
    icon: Lock,
    label: "Role-Based Permission Architecture",
    desc: "38 professional roles, 7-layer permission model including capabilities, authority levels, functional groups, and department context. Granular, governed access to every function.",
  },
  {
    icon: Database,
    label: "GDPR-Compliant Data Architecture",
    desc: "UK data hosting, automated right-to-erasure workflows, consent logging, DPA documentation, and data processing agreements as standard.",
  },
];

const SECURITY_CARDS = [
  { icon: Lock, label: "TLS 1.3 + AES-256", desc: "All data encrypted in transit and at rest. Credentials hashed and salted. No plain-text credential storage." },
  { icon: Globe, label: "UK Data Hosting", desc: "All data stored exclusively in UK-based infrastructure under UK GDPR. No international data transfers." },
  { icon: Database, label: "Automated Backups", desc: "Daily encrypted backups with point-in-time recovery. 30-day retention. Tested restore procedures." },
  { icon: Eye, label: "Immutable Audit Logs", desc: "Every action logged with user, timestamp, IP, and session. No overwriting. No gap in the record." },
  { icon: UserCheck, label: "7-Layer RBAC", desc: "Role, capability, authority, department, functional group, operational role, and tenant-level access control." },
  { icon: WifiOff, label: "Offline-First Architecture", desc: "Critical workflows continue without connectivity. Data queues locally and syncs automatically when reconnected." },
  { icon: Server, label: "99.9% Uptime SLA", desc: "Enterprise-grade infrastructure with load balancing, failover, and continuous health monitoring." },
  { icon: ShieldCheck, label: "UK GDPR Compliant", desc: "Data Processing Agreements, right-to-erasure, consent management, DPO support, and DPIA templates as standard." },
];

const WHY_ROWS = [
  {
    problem: "Disconnected tools create operational risk",
    solution: "One platform connects CAD, clinical, fleet, governance, and workforce. No copy-pasting. No version drift. No information loss between systems.",
  },
  {
    problem: "Legacy systems weren't designed for frontline environments",
    solution: "Offline-first architecture, mobile-optimised workflows, and field-ready interfaces — designed for ambulances, fire engines, and SAR vehicles before boardrooms.",
  },
  {
    problem: "Compliance is increasingly demanding",
    solution: "CQC, NHS data standards, UK GDPR, and clinical audit are architecturally embedded. Audit readiness is the default state — not a quarterly exercise.",
  },
  {
    problem: "Single-sector tools don't scale with operational complexity",
    solution: "38 professional roles across clinical, fire, SAR, security, logistics, and corporate functions. One platform grows with your operational scope.",
  },
];

const ONBOARDING_STEPS = [
  { num: "01", label: "Operational Assessment", desc: "We analyse your current workflows, compliance obligations, team structure, and technology environment before any configuration begins." },
  { num: "02", label: "Platform Configuration", desc: "Your workspace is built around your operational model. Roles, permissions, templates, workflows, and modules configured to your service." },
  { num: "03", label: "Data Migration", desc: "Staff records, vehicle registers, incident history, and training data migrated cleanly. Nothing manually re-entered." },
  { num: "04", label: "Role-Specific Training", desc: "Tailored training for crew, command, managers, and clinical leads. Live sessions, recorded walkthroughs, and on-demand guides." },
  { num: "05", label: "Supported Go-Live", desc: "An OpsCom implementation team member is available throughout your first operational period. No going live unsupported." },
  { num: "06", label: "Continuous Support", desc: "UK-based support with direct escalation for operational-critical issues. No chatbots. No automated ticket queues for emergencies." },
];

const PRICING_PLANS = [
  {
    name: "Essential",
    price: "£149",
    period: "/month",
    desc: "For smaller operational services establishing their digital infrastructure",
    highlight: false,
    features: [
      "Up to 15 staff accounts",
      "Digital PRF system",
      "Shift management",
      "Incident reporting",
      "Fleet inspections",
      "Email support",
    ],
    cta: "Request a Demonstration",
    ctaHref: "#request-demo",
  },
  {
    name: "Professional",
    price: "£299",
    period: "/month",
    desc: "For growing services with multi-domain operational requirements",
    highlight: true,
    features: [
      "Up to 60 staff accounts",
      "Everything in Essential",
      "CAD dispatch system",
      "Full fleet management",
      "HR & people module",
      "Training Hub (CPD, competencies, matrix)",
      "Documentation Hub (register, read receipts)",
      "Governance & risk tools",
      "Automated notifications",
      "Priority support",
    ],
    cta: "Request a Demonstration",
    ctaHref: "#request-demo",
  },
  {
    name: "Enterprise",
    price: "£499",
    period: "/month",
    desc: "Full platform for large, multi-site or multi-agency services",
    highlight: false,
    features: [
      "Unlimited staff accounts",
      "Everything in Professional",
      "CQC compliance workflows",
      "Operational intelligence",
      "Custom branding",
      "Full Training & Documentation Hubs",
      "Fire/SAR/Security modules",
      "Dedicated account manager",
      "SLA guarantee",
    ],
    cta: "Contact Enterprise Sales",
    ctaHref: "#request-demo",
  },
];

const FOOTER_PLATFORM = [
  { label: "Command & Control", href: "/platform/dispatch" },
  { label: "Clinical Operations", href: "/platform/clinical" },
  { label: "Workforce Management", href: "/platform/workforce" },
  { label: "Governance & Compliance", href: "/platform/compliance" },
  { label: "Fleet & Assets", href: "/platform/fleet" },
  { label: "Operational Intelligence", href: "/platform/intelligence" },
];

const FOOTER_SOLUTIONS = [
  { label: "Event Medical", href: "#solutions" },
  { label: "Ambulance Services", href: "#solutions" },
  { label: "Fire & Rescue", href: "#solutions" },
  { label: "Search & Rescue", href: "#solutions" },
  { label: "Security Operations", href: "#solutions" },
  { label: "Industrial Response", href: "#solutions" },
];

const FOOTER_ECOSYSTEM = [
  { label: "Tenant Workspace", href: "#" },
  { label: "Customer Portal", href: "#" },
  { label: "Client Portal", href: "#" },
  { label: "MDT App", href: "#" },
  { label: "Apply to Join", href: "/apply" },
];

const FOOTER_COMPANY = [
  { label: "About OpsCom", href: "#" },
  { label: "Security", href: "#security" },
  { label: "Pricing", href: "#pricing" },
  { label: "Contact", href: "#request-demo" },
  { label: "Privacy Policy", href: "#" },
  { label: "Terms of Service", href: "#" },
];

const PUBLIC_EMAILS = [
  { label: "General Enquiries", addr: "hello@opscom.co.uk" },
  { label: "Technical Support", addr: "support@opscom.co.uk" },
  { label: "Demonstrations & Sales", addr: "demos@opscom.co.uk" },
];

// ─── Subcomponents ────────────────────────────────────────────────────────────

function DispatchMockup() {
  return (
    <div className="relative mt-16 mx-auto max-w-4xl">
      <div className="absolute -bottom-10 left-1/2 -translate-x-1/2 w-3/4 h-20 bg-blue-600/15 blur-3xl rounded-full" />
      <div className="rounded-2xl border border-white/12 bg-slate-900 overflow-hidden shadow-2xl shadow-black/70 ring-1 ring-white/5">
        <div className="flex items-center justify-between px-4 py-2.5 bg-slate-800/80 border-b border-white/8">
          <div className="flex items-center gap-3">
            <div className="flex gap-1.5">
              <span className="h-2.5 w-2.5 rounded-full bg-red-500/50" />
              <span className="h-2.5 w-2.5 rounded-full bg-yellow-500/50" />
              <span className="h-2.5 w-2.5 rounded-full bg-green-500/50" />
            </div>
            <span className="text-[11px] text-slate-500 font-mono">OpsCom · Strategic Command Centre</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-[9px] text-slate-600">OCK · 11 May 2026 · 14:32</span>
            <span className="flex items-center gap-1.5 text-[10px] text-emerald-500 font-semibold">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />
              Live
            </span>
          </div>
        </div>
        <div className="grid grid-cols-[180px_1fr] sm:grid-cols-[200px_1fr] divide-x divide-white/8">
          <div className="p-4 space-y-1">
            <p className="text-[9px] font-bold text-slate-500 uppercase tracking-widest mb-3">Active Units</p>
            {[
              { name: "Alpha 1", status: "Available", dot: "bg-emerald-500", color: "text-emerald-400" },
              { name: "Bravo 2", status: "Attending", dot: "bg-blue-500", color: "text-blue-400" },
              { name: "Charlie 3", status: "At Hospital", dot: "bg-violet-500", color: "text-violet-400" },
              { name: "Delta 1", status: "Standby", dot: "bg-amber-500", color: "text-amber-400" },
            ].map((u) => (
              <div key={u.name} className="flex items-center justify-between py-1.5 border-b border-white/5 last:border-0">
                <div className="flex items-center gap-2">
                  <span className={`h-1.5 w-1.5 rounded-full shrink-0 ${u.dot}`} />
                  <span className="text-[11px] font-semibold text-white">{u.name}</span>
                </div>
                <span className={`text-[9px] font-medium ${u.color}`}>{u.status}</span>
              </div>
            ))}
            <div className="mt-4 rounded-lg bg-white/5 border border-white/8 p-3">
              <p className="text-[8px] text-slate-500 uppercase tracking-widest mb-2 font-bold">Crew Readiness</p>
              <div className="flex items-center gap-2 mb-1">
                <div className="flex-1 h-1 rounded-full bg-slate-700">
                  <div className="h-1 rounded-full bg-emerald-500 w-[92%]" />
                </div>
                <span className="text-[9px] text-emerald-400 font-bold">92%</span>
              </div>
              <p className="text-[8px] text-slate-600">12/13 crew compliant</p>
            </div>
          </div>
          <div className="p-4">
            <div className="flex items-center justify-between mb-3">
              <p className="text-[9px] font-bold text-slate-500 uppercase tracking-widest">Active Incidents</p>
              <span className="text-[9px] text-slate-600 font-mono">Refreshed 8s ago</span>
            </div>
            <div className="space-y-0">
              {[
                { priority: "P1", badge: "bg-red-600", desc: "Cardiac Arrest", loc: "42 Market Street, EC2A", unit: "Alpha 1", eta: "En Route · 3 min", prf: "OCK-20260511-0042", pfrColor: "text-red-400" },
                { priority: "P2", badge: "bg-amber-600", desc: "RTC with Injuries", loc: "A40 Junction 7, W3", unit: "Bravo 2", eta: "On Scene", prf: "OCK-20260511-0041", pfrColor: "text-amber-400" },
                { priority: "P3", badge: "bg-blue-700", desc: "Fall, Elderly Patient", loc: "Elm Park Estate, W14", unit: "Unassigned", eta: "Pending", prf: "—", pfrColor: "text-slate-600" },
              ].map((inc) => (
                <div key={inc.desc} className="flex items-start gap-3 py-3 border-b border-white/5 last:border-0">
                  <span className={`text-[8px] font-extrabold text-white px-1.5 py-0.5 rounded shrink-0 mt-0.5 ${inc.badge}`}>{inc.priority}</span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2 mb-0.5">
                      <p className="text-[11px] font-bold text-white">{inc.desc}</p>
                      <span className={`text-[9px] shrink-0 font-mono ${inc.pfrColor}`}>{inc.prf}</span>
                    </div>
                    <p className="text-[9px] text-slate-500 mb-1">{inc.loc}</p>
                    <div className="flex items-center gap-3">
                      <span className="text-[9px] font-semibold text-slate-400">{inc.unit}</span>
                      <span className="text-[9px] text-slate-600">{inc.eta}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-x-5 gap-y-1 px-4 py-2 bg-slate-800/60 border-t border-white/8 text-[9px] text-slate-500">
          <span className="flex items-center gap-1.5"><span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />4 units operational</span>
          <span>3 incidents active</span>
          <span>2 PRFs in progress</span>
          <span className="ml-auto font-mono text-slate-700">OpsCom Platform · Ockham Emergency Services</span>
        </div>
      </div>
    </div>
  );
}

function GovernanceMockup() {
  return (
    <div className="relative mx-auto max-w-4xl">
      <div className="absolute -bottom-10 left-1/2 -translate-x-1/2 w-3/4 h-20 bg-purple-600/15 blur-3xl rounded-full" />
      <div className="rounded-2xl border border-white/12 bg-slate-900 overflow-hidden shadow-2xl shadow-black/70 ring-1 ring-white/5">
        <div className="flex items-center justify-between px-4 py-2.5 bg-slate-800/80 border-b border-white/8">
          <div className="flex items-center gap-3">
            <div className="flex gap-1.5">
              <span className="h-2.5 w-2.5 rounded-full bg-red-500/50" />
              <span className="h-2.5 w-2.5 rounded-full bg-yellow-500/50" />
              <span className="h-2.5 w-2.5 rounded-full bg-green-500/50" />
            </div>
            <span className="text-[11px] text-slate-500 font-mono">OpsCom · Governance &amp; Compliance — Ockham Emergency Services</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-[9px] text-slate-600">11 May 2026 · 14:35</span>
            <span className="flex items-center gap-1.5 text-[10px] text-emerald-500 font-semibold">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />
              Live
            </span>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 divide-y sm:divide-y-0 sm:divide-x divide-white/8">
          <div className="p-5 space-y-3">
            <p className="text-[9px] font-bold text-slate-500 uppercase tracking-widest mb-4">Compliance KPIs</p>
            {[
              { label: "CQC Readiness Score", value: "87%", bar: "w-[87%]", barColor: "bg-emerald-500", textColor: "text-emerald-400", badge: "On Track" },
              { label: "Training Compliance", value: "94%", bar: "w-[94%]", barColor: "bg-blue-500", textColor: "text-blue-400", badge: "On Track" },
            ].map((kpi) => (
              <div key={kpi.label} className="rounded-lg bg-white/5 border border-white/8 p-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[10px] font-medium text-slate-300">{kpi.label}</span>
                  <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded bg-emerald-500/15 ${kpi.textColor}`}>{kpi.badge}</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="flex-1 h-1.5 rounded-full bg-slate-700">
                    <div className={`h-1.5 rounded-full ${kpi.barColor} ${kpi.bar}`} />
                  </div>
                  <span className={`text-[11px] font-extrabold ${kpi.textColor}`}>{kpi.value}</span>
                </div>
              </div>
            ))}
            {[
              { label: "Open Incidents", value: "2", color: "text-amber-400", dot: "bg-amber-500", badgeBg: "bg-amber-500/15", badge: "Review Required" },
              { label: "Risk Score", value: "LOW", color: "text-emerald-400", dot: "bg-emerald-500", badgeBg: "bg-emerald-500/15", badge: "Within Threshold" },
            ].map((stat) => (
              <div key={stat.label} className="flex items-center justify-between rounded-lg bg-white/5 border border-white/8 px-3 py-2.5">
                <span className="text-[10px] font-medium text-slate-300">{stat.label}</span>
                <div className="flex items-center gap-2">
                  <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${stat.badgeBg} ${stat.color}`}>{stat.badge}</span>
                  <span className={`text-[13px] font-extrabold ${stat.color}`}>{stat.value}</span>
                </div>
              </div>
            ))}
          </div>
          <div className="p-5">
            <p className="text-[9px] font-bold text-slate-500 uppercase tracking-widest mb-4">Recent Governance Activity</p>
            <div className="space-y-0">
              {[
                { icon: FileText, color: "text-blue-400", bg: "bg-blue-500/10", action: "Policy updated", detail: "P-031 — Medication Administration", status: "Completed", statusColor: "text-emerald-400", statusBg: "bg-emerald-500/10", time: "2h ago" },
                { icon: AlertOctagon, color: "text-amber-400", bg: "bg-amber-500/10", action: "Risk raised", detail: "Fleet — Vehicle VOR >72h", status: "Open", statusColor: "text-amber-400", statusBg: "bg-amber-500/10", time: "4h ago" },
                { icon: UserCheck, color: "text-purple-400", bg: "bg-purple-500/10", action: "Training recorded", detail: "ILS Refresher — 4 staff", status: "Logged", statusColor: "text-blue-400", statusBg: "bg-blue-500/10", time: "6h ago" },
                { icon: ClipboardList, color: "text-emerald-400", bg: "bg-emerald-500/10", action: "Audit completed", detail: "Q2 Clinical Standards Review", status: "Passed", statusColor: "text-emerald-400", statusBg: "bg-emerald-500/10", time: "1d ago" },
                { icon: ShieldCheck, color: "text-cyan-400", bg: "bg-cyan-500/10", action: "CQC evidence filed", detail: "Quality Statement 4.3 — Evidence Pack", status: "Submitted", statusColor: "text-cyan-400", statusBg: "bg-cyan-500/10", time: "2d ago" },
              ].map((item) => (
                <div key={item.detail} className="flex items-start gap-3 py-2.5 border-b border-white/5 last:border-0">
                  <span className={`h-6 w-6 rounded-md ${item.bg} flex items-center justify-center shrink-0 mt-0.5`}>
                    <item.icon className={`h-3 w-3 ${item.color}`} />
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2 mb-0.5">
                      <p className="text-[10px] font-semibold text-white">{item.action}</p>
                      <span className={`text-[8px] font-semibold px-1.5 py-0.5 rounded ${item.statusBg} ${item.statusColor} shrink-0`}>{item.status}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <p className="text-[9px] text-slate-500 truncate">{item.detail}</p>
                      <span className="text-[8px] text-slate-700 shrink-0 ml-2">{item.time}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-x-5 gap-y-1 px-4 py-2 bg-slate-800/60 border-t border-white/8 text-[9px] text-slate-500">
          <span className="flex items-center gap-1.5"><span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />All critical actions resolved</span>
          <span>5 governance events today</span>
          <span>Audit log: 847 entries</span>
          <span className="ml-auto font-mono text-slate-700">OpsCom Platform · Ockham Emergency Services</span>
        </div>
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function MarketingPage() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [platformOpen, setPlatformOpen] = useState(false);
  const [solutionsOpen, setSolutionsOpen] = useState(false);
  const [demoForm, setDemoForm] = useState({
    name: "",
    organisation: "",
    email: "",
    phone: "",
    sector: "",
    message: "",
  });
  const [demoSubmitting, setDemoSubmitting] = useState(false);
  const [demoSubmitted, setDemoSubmitted] = useState(false);

  async function handleDemoSubmit(e: React.FormEvent) {
    e.preventDefault();
    setDemoSubmitting(true);
    try {
      await fetch("/api/request-demo", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(demoForm),
      });
      setDemoSubmitted(true);
    } catch {
      setDemoSubmitted(true);
    } finally {
      setDemoSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen bg-slate-950 text-white antialiased">

      {/* ── NAV ── */}
      <nav className="sticky top-0 z-50 bg-slate-950/95 backdrop-blur-md border-b border-white/8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-10">
              <Link href="/" className="flex items-center gap-2 shrink-0">
                <img src="/opscom.png" alt="OpsCom" className="h-9 w-auto" />
              </Link>
              <div className="hidden lg:flex items-center gap-1">
                <div
                  className="relative"
                  onMouseEnter={() => setPlatformOpen(true)}
                  onMouseLeave={() => setPlatformOpen(false)}
                >
                  <button className="flex items-center gap-1 px-3 py-2 text-sm text-slate-300 hover:text-white rounded-lg hover:bg-white/5 transition-colors">
                    Platform <ChevronDown className="h-3.5 w-3.5 opacity-60" />
                  </button>
                  {platformOpen && (
                    <div className="absolute top-full left-0 mt-1 w-56 rounded-xl border border-white/10 bg-slate-900 shadow-xl shadow-black/40 p-1.5">
                      {PLATFORM_LINKS.map((item) => (
                        <Link
                          key={item.href}
                          href={item.href}
                          className="flex items-center justify-between px-3 py-2 text-sm text-slate-300 hover:text-white hover:bg-white/5 rounded-lg transition-colors"
                        >
                          {item.label}
                          <ChevronRight className="h-3.5 w-3.5 opacity-40" />
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
                <div
                  className="relative"
                  onMouseEnter={() => setSolutionsOpen(true)}
                  onMouseLeave={() => setSolutionsOpen(false)}
                >
                  <button className="flex items-center gap-1 px-3 py-2 text-sm text-slate-300 hover:text-white rounded-lg hover:bg-white/5 transition-colors">
                    Solutions <ChevronDown className="h-3.5 w-3.5 opacity-60" />
                  </button>
                  {solutionsOpen && (
                    <div className="absolute top-full left-0 mt-1 w-56 rounded-xl border border-white/10 bg-slate-900 shadow-xl shadow-black/40 p-1.5">
                      {SOLUTION_LINKS.map((item) => (
                        <a
                          key={item.label}
                          href={item.href}
                          className="flex items-center justify-between px-3 py-2 text-sm text-slate-300 hover:text-white hover:bg-white/5 rounded-lg transition-colors"
                        >
                          {item.label}
                          <ChevronRight className="h-3.5 w-3.5 opacity-40" />
                        </a>
                      ))}
                    </div>
                  )}
                </div>
                <a href="#governance" className="px-3 py-2 text-sm text-slate-300 hover:text-white rounded-lg hover:bg-white/5 transition-colors">Governance</a>
                <a href="#security" className="px-3 py-2 text-sm text-slate-300 hover:text-white rounded-lg hover:bg-white/5 transition-colors">Security</a>
                <a href="#pricing" className="px-3 py-2 text-sm text-slate-300 hover:text-white rounded-lg hover:bg-white/5 transition-colors">Pricing</a>
              </div>
            </div>
            <div className="hidden lg:flex items-center gap-3">
              <a
                href="https://demo.opscom.co.uk/login"
                className="px-3 py-2 text-sm text-slate-300 hover:text-white transition-colors"
              >
                Sign in
              </a>
              <a
                href="#request-demo"
                className="px-4 py-2 text-sm font-semibold bg-blue-600 hover:bg-blue-500 text-white rounded-lg transition-colors shadow-lg shadow-blue-600/25"
              >
                Request a Demonstration
              </a>
            </div>
            <button
              className="lg:hidden p-2 text-slate-400 hover:text-white"
              onClick={() => setMobileOpen(!mobileOpen)}
              aria-label="Toggle menu"
            >
              {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>
        {mobileOpen && (
          <div className="lg:hidden border-t border-white/8 bg-slate-950 px-4 py-4 space-y-1">
            <p className="text-[10px] font-bold text-slate-600 uppercase tracking-widest px-3 mb-2">Platform</p>
            {PLATFORM_LINKS.map((item) => (
              <Link key={item.href} href={item.href} onClick={() => setMobileOpen(false)} className="block px-3 py-2 text-sm text-slate-300 hover:text-white hover:bg-white/5 rounded-lg">
                {item.label}
              </Link>
            ))}
            <p className="text-[10px] font-bold text-slate-600 uppercase tracking-widest px-3 mt-3 mb-2">Solutions</p>
            {SOLUTION_LINKS.map((item) => (
              <a key={item.label} href={item.href} onClick={() => setMobileOpen(false)} className="block px-3 py-2 text-sm text-slate-300 hover:text-white hover:bg-white/5 rounded-lg">
                {item.label}
              </a>
            ))}
            <div className="pt-3 border-t border-white/8 flex flex-col gap-2 mt-2">
              <a href="https://demo.opscom.co.uk/login" className="px-3 py-2 text-sm text-slate-300 hover:text-white">Sign in</a>
              <a href="#request-demo" onClick={() => setMobileOpen(false)} className="px-4 py-2.5 text-sm font-semibold bg-blue-600 hover:bg-blue-500 text-white rounded-lg text-center">
                Request a Demonstration
              </a>
            </div>
          </div>
        )}
      </nav>

      {/* ── HERO ── */}
      <section className="relative overflow-hidden bg-slate-950 pt-20 pb-8">
        <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:64px_64px]" />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[500px] bg-blue-600/10 blur-[120px] rounded-full pointer-events-none" />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-blue-500/30 bg-blue-500/10 text-blue-300 text-xs font-medium mb-8">
            <span className="h-1.5 w-1.5 rounded-full bg-blue-400 animate-pulse" />
            Operational Command Ecosystem &middot; Built by frontline professionals
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-tight mb-6">
            <span className="text-white block">The operational command platform</span>
            <span className="block bg-gradient-to-r from-blue-400 via-blue-300 to-cyan-400 bg-clip-text text-transparent">
              built by frontline professionals.
            </span>
          </h1>
          <p className="max-w-3xl mx-auto text-lg text-slate-400 leading-relaxed mb-10">
            OpsCom spans the complete operational lifecycle — from pre-event command planning and resource readiness through live CAD dispatch and incident command, to field MDT, digital patient records, and post-event analysis. One platform. Every layer of operations.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mb-14">
            <a
              href="#request-demo"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-500 text-white font-semibold rounded-xl transition-colors shadow-xl shadow-blue-600/30 text-sm"
            >
              Request a Platform Demonstration
              <ArrowRight className="h-4 w-4" />
            </a>
            <a
              href="https://demo.opscom.co.uk"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3 border border-white/15 hover:border-white/30 bg-white/5 text-white font-semibold rounded-xl transition-colors text-sm"
            >
              Explore the Platform
              <ExternalLink className="h-4 w-4 opacity-60" />
            </a>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-8 mb-6">
            {TRUST_STATS.map((s) => (
              <div key={s.label} className="text-center">
                <p className="text-2xl font-extrabold text-white">{s.value}</p>
                <p className="text-xs text-slate-500 mt-0.5">{s.label}</p>
              </div>
            ))}
          </div>
          <DispatchMockup />
        </div>
      </section>

      {/* ── THREE OPERATIONAL LAYERS ── */}
      <section className="py-20 bg-slate-950">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <p className="text-[11px] font-bold text-blue-400 uppercase tracking-widest mb-3">Platform Architecture</p>
            <h2 className="text-2xl sm:text-3xl font-extrabold text-white mb-4">Three layers. One connected platform.</h2>
            <p className="max-w-2xl mx-auto text-slate-400 text-base">
              OpsCom is the only platform that covers every stage of emergency service and event operations — from strategic planning through live command to field response.
            </p>
          </div>
          <div className="relative">
            {/* Connector line */}
            <div className="absolute top-1/2 left-0 right-0 h-px bg-gradient-to-r from-transparent via-blue-500/30 to-transparent hidden lg:block" />
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {[
                {
                  layer: "LAYER 1",
                  label: "Event Planning & Command Prep",
                  icon: ClipboardList,
                  color: "text-violet-400",
                  glow: "shadow-violet-500/20",
                  border: "border-violet-500/30",
                  bg: "bg-violet-500/10",
                  desc: "Pre-event operational readiness. Medical Event Plans, staffing & resource allocation, risk assessment, Bronze/Silver/Gold command structure, hospital pathways, client sign-off, and multi-agency coordination.",
                  tags: ["Event Medical Plans", "Staffing & Readiness", "Risk Matrix", "Client Portal", "Command Structure"],
                  phase: "Pre-Event",
                  phaseColor: "bg-violet-500/20 text-violet-300 border-violet-500/30",
                },
                {
                  layer: "LAYER 2",
                  label: "CAD / Command & Control",
                  icon: Radio,
                  color: "text-blue-400",
                  glow: "shadow-blue-500/20",
                  border: "border-blue-500/30",
                  bg: "bg-blue-500/10",
                  desc: "Live operational command. CAD dispatch, unit tracking, incident management, METHANE & JESIP tools, major incident escalation, SITREP, command log, multi-agency coordination, and real-time tactical overview.",
                  tags: ["CAD Dispatch", "Unit Tracking", "Major Incident", "SITREP", "Command Log"],
                  phase: "Live Ops",
                  phaseColor: "bg-blue-500/20 text-blue-300 border-blue-500/30",
                },
                {
                  layer: "LAYER 3",
                  label: "MDT / Field Operations",
                  icon: Ambulance,
                  color: "text-emerald-400",
                  glow: "shadow-emerald-500/20",
                  border: "border-emerald-500/30",
                  bg: "bg-emerald-500/10",
                  desc: "Field-facing tools for responders. Navigation, digital PRFs with NEWS2, offline capability, medication administration, vehicle status, welfare checks, task updates, and post-incident reporting — from the vehicle.",
                  tags: ["Digital PRF", "Offline-First", "Navigation", "Welfare", "Status Updates"],
                  phase: "Field Response",
                  phaseColor: "bg-emerald-500/20 text-emerald-300 border-emerald-500/30",
                },
              ].map((layer, i) => (
                <div key={i} className={cn("relative rounded-2xl border p-6 shadow-xl group hover:shadow-2xl transition-shadow", layer.border, "bg-slate-900/80")}>
                  <div className="flex items-start justify-between mb-4">
                    <div className={cn("h-12 w-12 rounded-xl flex items-center justify-center", layer.bg)}>
                      <layer.icon className={cn("h-6 w-6", layer.color)} />
                    </div>
                    <span className={cn("text-[9px] font-black rounded-full border px-2.5 py-1 uppercase tracking-wider", layer.phaseColor)}>{layer.phase}</span>
                  </div>
                  <p className={cn("text-[10px] font-black uppercase tracking-widest mb-1", layer.color)}>{layer.layer}</p>
                  <h3 className="font-extrabold text-white text-lg leading-snug mb-3">{layer.label}</h3>
                  <p className="text-slate-400 text-sm leading-relaxed mb-4">{layer.desc}</p>
                  <div className="flex flex-wrap gap-1.5">
                    {layer.tags.map(tag => (
                      <span key={tag} className={cn("text-[10px] font-semibold rounded border px-2 py-0.5", layer.bg, layer.border, layer.color)}>{tag}</span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="mt-10 text-center">
            <div className="inline-flex items-center gap-3 rounded-2xl border border-white/10 bg-slate-900/60 px-6 py-4 text-sm text-slate-400">
              <ShieldCheck className="h-5 w-5 text-blue-400 shrink-0" />
              <span>All three layers share a <strong className="text-white">single data layer</strong> — incidents raised in CAD flow automatically into clinical records, governance, and post-event analytics.</span>
            </div>
          </div>
        </div>
      </section>

      {/* ── BRAND PILLARS ── */}
      <section className="py-16 border-y border-white/5 bg-slate-900/20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-[11px] font-bold text-blue-400 uppercase tracking-widest mb-3">Platform Principles</p>
            <h2 className="text-2xl sm:text-3xl font-extrabold text-white">Built around six founding principles.</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {BRAND_PILLARS.map((pillar) => (
              <div key={pillar.label} className="rounded-2xl border border-white/8 bg-white/[0.03] p-6 hover:bg-white/[0.05] transition-colors">
                <div className={`inline-flex items-center justify-center h-10 w-10 rounded-xl border ${pillar.bg} mb-4`}>
                  <pillar.icon className={`h-5 w-5 ${pillar.color}`} />
                </div>
                <h3 className="text-sm font-bold text-white mb-2">{pillar.label}</h3>
                <p className="text-xs text-slate-500 leading-relaxed">{pillar.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── WHAT IS OPSCOM ── */}
      <section className="py-24 bg-slate-900/30 relative">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-[11px] font-bold text-blue-400 uppercase tracking-widest mb-3">The Platform</p>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-white mb-4">Not software. Operational infrastructure.</h2>
            <p className="max-w-2xl mx-auto text-slate-400 text-lg">
              OpsCom is the operational system of record for high-risk organisations — replacing fragmented tools, paper processes, and legacy systems with a single governed platform.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              {
                icon: Command,
                color: "text-blue-400",
                bg: "bg-blue-500/10 border-blue-500/20",
                label: "Command & Control",
                desc: "Real-time CAD, crew status boards, MDT, multi-agency coordination, major incident management, and operational intelligence from a single command interface.",
              },
              {
                icon: Users,
                color: "text-emerald-400",
                bg: "bg-emerald-500/10 border-emerald-500/20",
                label: "Workforce & Governance",
                desc: "Shift management, HR records, training, competencies, CQC workflows, documentation, risk registers, and complete audit trails — built for regulated services.",
              },
              {
                icon: BarChart3,
                color: "text-purple-400",
                bg: "bg-purple-500/10 border-purple-500/20",
                label: "Intelligence & Assurance",
                desc: "Live KPI dashboards, compliance scoring, operational readiness, workforce analytics, and executive reporting — turning operations into evidence.",
              },
            ].map((col) => (
              <div key={col.label} className="rounded-2xl border border-white/8 bg-white/[0.03] p-7 hover:bg-white/[0.05] transition-colors">
                <div className={`inline-flex items-center justify-center h-11 w-11 rounded-xl border ${col.bg} mb-5`}>
                  <col.icon className={`h-5 w-5 ${col.color}`} />
                </div>
                <h3 className="text-lg font-bold text-white mb-3">{col.label}</h3>
                <p className="text-sm text-slate-400 leading-relaxed">{col.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── PLATFORM ARCHITECTURE ── */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:64px_64px]" />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-[11px] font-bold text-blue-400 uppercase tracking-widest mb-3">Platform Architecture</p>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-white mb-4">Five distinct layers. One connected ecosystem.</h2>
            <p className="max-w-2xl mx-auto text-slate-400 text-lg">
              OpsCom is architecturally separated into five distinct operational layers — each purpose-built, each connected.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {[
              { icon: Globe, color: "text-slate-400", bg: "bg-slate-500/10 border-slate-500/20", label: "Public Platform Website", desc: "Marketing, onboarding, trust layer and SaaS conversion", badge: "opscom.co.uk", primary: false },
              { icon: Building2, color: "text-blue-400", bg: "bg-blue-500/10 border-blue-500/20", label: "Customer Account Portal", desc: "Subscription management, billing, modules, licences, and organisation settings", badge: "account.opscom.co.uk", primary: false },
              { icon: LayoutDashboard, color: "text-blue-300", bg: "bg-blue-500/15 border-blue-400/40", label: "Tenant Operational Workspace", desc: "Command, dispatch, clinical, fleet, governance, workforce — the full operational platform", badge: "[tenant].opscom.co.uk", primary: true },
              { icon: Users, color: "text-emerald-400", bg: "bg-emerald-500/10 border-emerald-500/20", label: "Client Portal", desc: "Event organisers and external customers — booking, documents, live updates", badge: "portal.opscom.co.uk", primary: false },
              { icon: Radio, color: "text-purple-400", bg: "bg-purple-500/10 border-purple-500/20", label: "MDT / Responder App", desc: "Field crew: incidents, PRFs, status, briefings — offline-first mobile interface", badge: "MDT App", primary: false },
            ].map((layer) => (
              <div
                key={layer.label}
                className={cn(
                  "rounded-2xl border p-5 flex flex-col gap-3 transition-colors",
                  layer.primary
                    ? "border-blue-500/40 bg-blue-500/10 ring-1 ring-blue-500/20"
                    : "border-white/8 bg-white/[0.03] hover:bg-white/[0.05]"
                )}
              >
                <div className={`inline-flex items-center justify-center h-10 w-10 rounded-xl border ${layer.bg}`}>
                  <layer.icon className={`h-5 w-5 ${layer.color}`} />
                </div>
                <div>
                  <h3 className={cn("text-sm font-bold mb-1", layer.primary ? "text-blue-200" : "text-white")}>{layer.label}</h3>
                  <p className="text-xs text-slate-500 leading-relaxed">{layer.desc}</p>
                </div>
                <span className={cn(
                  "self-start text-[10px] font-mono px-2 py-1 rounded-md border",
                  layer.primary
                    ? "bg-blue-500/20 border-blue-400/30 text-blue-300"
                    : "bg-white/5 border-white/10 text-slate-500"
                )}>
                  {layer.badge}
                </span>
                {layer.primary && (
                  <span className="self-start text-[9px] font-bold text-blue-400 uppercase tracking-wider">Primary Layer</span>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── ECOSYSTEM OVERVIEW ── */}
      <section className="py-24 bg-slate-900/30">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-[11px] font-bold text-blue-400 uppercase tracking-widest mb-3">Integrated Ecosystem</p>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-white mb-4">Eight interconnected systems. One operational picture.</h2>
            <p className="max-w-2xl mx-auto text-slate-400 text-lg">
              Every system shares the same data layer. Actions in dispatch appear in clinical. Compliance triggers appear in governance. Everything is connected, everything is audited.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {ECOSYSTEM_MODULES.map((mod) => (
              <div key={mod.label} className="rounded-2xl border border-white/8 bg-white/[0.03] p-5 hover:bg-white/[0.05] transition-colors">
                <div className={`inline-flex items-center justify-center h-10 w-10 rounded-xl border ${mod.bg} mb-4`}>
                  <mod.icon className={`h-5 w-5 ${mod.color}`} />
                </div>
                <h3 className="text-sm font-bold text-white mb-2">{mod.label}</h3>
                <p className="text-xs text-slate-500 leading-relaxed">{mod.desc}</p>
              </div>
            ))}
          </div>
          <div className="mt-12 text-center">
            <div className="inline-flex items-center gap-3 text-sm text-slate-500">
              <span className="h-px w-12 bg-slate-700" />
              <span className="flex items-center gap-2">
                <Network className="h-4 w-4 text-blue-500/60" />
                Powered by a single real-time data layer
              </span>
              <span className="h-px w-12 bg-slate-700" />
            </div>
          </div>
        </div>
      </section>

      {/* ── SOLUTIONS BY SECTOR ── */}
      <section id="solutions" className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:64px_64px]" />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-[11px] font-bold text-blue-400 uppercase tracking-widest mb-3">Solutions by Sector</p>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-white mb-4">Built for the most demanding operational environments.</h2>
            <p className="max-w-2xl mx-auto text-slate-400 text-lg">
              OpsCom is deployed across ambulance services, fire and rescue, search and rescue, security operations, and industrial response. Each deployment is configured around your operational model.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {SECTORS.map((sector) => (
              <a
                key={sector.label}
                href="#request-demo"
                className={cn(
                  "group rounded-2xl border border-white/8 bg-white/[0.03] p-6 hover:bg-white/[0.06] transition-all hover:shadow-xl",
                  sector.glow
                )}
              >
                <div className="flex items-start justify-between gap-4 mb-4">
                  <div className="flex items-center gap-3">
                    <div className={`inline-flex items-center justify-center h-10 w-10 rounded-xl border ${sector.bg}`}>
                      <sector.icon className={`h-5 w-5 ${sector.color}`} />
                    </div>
                    <h3 className="text-base font-bold text-white">{sector.label}</h3>
                  </div>
                  <ChevronRight className="h-5 w-5 text-slate-600 group-hover:text-slate-400 transition-colors shrink-0 mt-0.5" />
                </div>
                <p className="text-sm text-slate-400 leading-relaxed mb-4">{sector.desc}</p>
                <div className="flex flex-wrap gap-2">
                  {sector.tags.map((tag) => (
                    <span key={tag} className="text-[10px] font-medium px-2 py-1 rounded-md bg-white/5 border border-white/8 text-slate-400">
                      {tag}
                    </span>
                  ))}
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* ── GOVERNANCE & COMPLIANCE ── */}
      <section id="governance" className="py-24 relative overflow-hidden bg-slate-900/50">
        <div className="absolute top-0 right-0 w-[600px] h-[500px] bg-purple-600/8 blur-[120px] rounded-full pointer-events-none" />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-[11px] font-bold text-purple-400 uppercase tracking-widest mb-3">Governance-First Architecture</p>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-white mb-4">Compliance isn't a feature. It's the foundation.</h2>
            <p className="max-w-2xl mx-auto text-slate-400 text-lg">
              Every action on the OpsCom platform is logged, attributed, and timestamped. Inspection readiness is the default state — not something you prepare for.
            </p>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            <div className="space-y-6">
              {GOVERNANCE_FEATURES.map((feat) => (
                <div key={feat.label} className="flex gap-4">
                  <div className="inline-flex items-center justify-center h-10 w-10 rounded-xl border border-purple-500/20 bg-purple-500/10 shrink-0 mt-0.5">
                    <feat.icon className="h-5 w-5 text-purple-400" />
                  </div>
                  <div>
                    <h3 className="text-base font-bold text-white mb-1.5">{feat.label}</h3>
                    <p className="text-sm text-slate-400 leading-relaxed">{feat.desc}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="rounded-2xl border border-white/10 bg-slate-900 overflow-hidden shadow-xl shadow-black/40">
              <div className="px-4 py-3 border-b border-white/8 bg-slate-800/60">
                <p className="text-xs font-bold text-slate-400">Governance Overview — Ockham Emergency Services</p>
              </div>
              <div className="p-5 space-y-3">
                {[
                  { label: "CQC Readiness Score", value: "87%", bar: true, barWidth: "w-[87%]", barColor: "bg-emerald-500", valueColor: "text-emerald-400" },
                  { label: "Training Compliance", value: "94%", bar: true, barWidth: "w-[94%]", barColor: "bg-blue-500", valueColor: "text-blue-400" },
                ].map((item) => (
                  <div key={item.label} className="rounded-lg bg-white/5 border border-white/8 p-3">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs text-slate-300">{item.label}</span>
                      <span className={`text-sm font-bold ${item.valueColor}`}>{item.value}</span>
                    </div>
                    <div className="h-1.5 rounded-full bg-slate-700">
                      <div className={`h-1.5 rounded-full ${item.barColor} ${item.barWidth}`} />
                    </div>
                  </div>
                ))}
                {[
                  { label: "Outstanding Actions", value: "3", color: "text-amber-400", dot: "bg-amber-500" },
                  { label: "Documents Requiring Acknowledgement", value: "2", color: "text-amber-400", dot: "bg-amber-500" },
                  { label: "Last Risk Review", value: "2 days ago", color: "text-emerald-400", dot: "bg-emerald-500" },
                  { label: "Incident Register", value: "0 open critical", color: "text-emerald-400", dot: "bg-emerald-500" },
                ].map((item) => (
                  <div key={item.label} className="flex items-center justify-between rounded-lg bg-white/5 border border-white/8 px-3 py-2.5">
                    <div className="flex items-center gap-2">
                      <span className={`h-1.5 w-1.5 rounded-full ${item.dot}`} />
                      <span className="text-xs text-slate-400">{item.label}</span>
                    </div>
                    <span className={`text-xs font-bold ${item.color}`}>{item.value}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── OPERATIONAL ASSURANCE ── */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:64px_64px]" />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-[11px] font-bold text-blue-400 uppercase tracking-widest mb-3">Operational Assurance</p>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-white mb-4">Executive visibility across every operational domain.</h2>
            <p className="max-w-2xl mx-auto text-slate-400 text-lg">
              From director to dispatcher, OpsCom provides the right level of visibility for every role — without exposing sensitive data outside appropriate permission tiers.
            </p>
          </div>
          <GovernanceMockup />
        </div>
      </section>

      {/* ── SECURITY ── */}
      <section id="security" className="py-24 bg-slate-900/30">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-[11px] font-bold text-blue-400 uppercase tracking-widest mb-3">Security & Infrastructure</p>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-white mb-4">Enterprise-grade infrastructure. Operational resilience.</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {SECURITY_CARDS.map((card) => (
              <div key={card.label} className="rounded-2xl border border-white/8 bg-white/[0.03] p-5 hover:bg-white/[0.05] transition-colors">
                <div className="inline-flex items-center justify-center h-9 w-9 rounded-lg border border-blue-500/20 bg-blue-500/10 mb-4">
                  <card.icon className="h-4 w-4 text-blue-400" />
                </div>
                <h3 className="text-sm font-bold text-white mb-2">{card.label}</h3>
                <p className="text-xs text-slate-500 leading-relaxed">{card.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── WHY OPSCOM ── */}
      <section id="why-opscom" className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:64px_64px]" />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-[11px] font-bold text-blue-400 uppercase tracking-widest mb-3">Why OpsCom</p>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-white mb-4">Operational infrastructure. Not another tool.</h2>
            <p className="max-w-2xl mx-auto text-slate-400 text-lg">
              Most platforms in this space were built for one use case and retrofitted for others. OpsCom was architected from the ground up as an enterprise operational ecosystem.
            </p>
          </div>
          <div className="space-y-4">
            {WHY_ROWS.map((row, i) => (
              <div key={i} className="grid grid-cols-1 md:grid-cols-2 gap-0 rounded-2xl overflow-hidden border border-white/8">
                <div className="flex items-start gap-4 p-6 bg-red-500/5 border-b md:border-b-0 md:border-r border-white/8">
                  <div className="h-7 w-7 rounded-full bg-red-500/15 border border-red-500/20 flex items-center justify-center shrink-0 mt-0.5">
                    <X className="h-3.5 w-3.5 text-red-400" />
                  </div>
                  <p className="text-sm text-slate-400 leading-relaxed">{row.problem}</p>
                </div>
                <div className="flex items-start gap-4 p-6 bg-emerald-500/5">
                  <div className="h-7 w-7 rounded-full bg-emerald-500/15 border border-emerald-500/20 flex items-center justify-center shrink-0 mt-0.5">
                    <Check className="h-3.5 w-3.5 text-emerald-400" />
                  </div>
                  <p className="text-sm text-slate-300 leading-relaxed">{row.solution}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── OPERATIONALLY INFORMED ── */}
      <section className="py-24 bg-slate-900/50 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-[500px] h-[400px] bg-blue-600/6 blur-[120px] rounded-full pointer-events-none" />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-[11px] font-bold text-blue-400 uppercase tracking-widest mb-3">Operationally Informed</p>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-white mb-4">Designed for environments where software failures have real consequences.</h2>
            <p className="max-w-3xl mx-auto text-slate-400 text-lg leading-relaxed">
              OpsCom was designed alongside operational professionals who understand that a slow interface, a missing field, or an offline failure during an incident isn&apos;t an inconvenience — it&apos;s a risk. Every design decision reflects that understanding.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {OPERATIONAL_ENV.map((item) => (
              <div key={item.label} className="rounded-2xl border border-white/8 bg-white/[0.03] p-6 hover:bg-white/[0.05] transition-colors">
                <div className="inline-flex items-center justify-center h-10 w-10 rounded-xl border border-blue-500/20 bg-blue-500/10 mb-4">
                  <item.icon className="h-5 w-5 text-blue-400" />
                </div>
                <h3 className="text-sm font-bold text-white mb-2">{item.label}</h3>
                <p className="text-xs text-slate-500 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
          <div className="mt-12 rounded-2xl border border-blue-500/15 bg-blue-500/5 p-8 text-center">
            <p className="text-slate-300 text-base leading-relaxed max-w-3xl mx-auto">
              <span className="text-white font-semibold">"Purpose-built for live operations."</span> — Every interface, every workflow, and every data structure was designed around real operational requirements: the shift briefing, the P1 call, the post-incident debrief, and the quarterly CQC inspection.
            </p>
          </div>
        </div>
      </section>

      {/* ── ONBOARDING ── */}
      <section className="py-24 bg-slate-900/30">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-[11px] font-bold text-blue-400 uppercase tracking-widest mb-3">Enterprise Onboarding</p>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-white mb-4">Operational from day one.</h2>
            <p className="max-w-2xl mx-auto text-slate-400 text-lg">
              OpsCom deployments are structured as enterprise implementations — not self-service sign-ups. We configure the platform around your service, migrate your data, and support your go-live.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {ONBOARDING_STEPS.map((step) => (
              <div key={step.num} className="rounded-2xl border border-white/8 bg-white/[0.03] p-6 hover:bg-white/[0.05] transition-colors">
                <span className="text-4xl font-black text-slate-800 font-mono block mb-4">{step.num}</span>
                <h3 className="text-base font-bold text-white mb-2">{step.label}</h3>
                <p className="text-sm text-slate-400 leading-relaxed">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── PRICING ── */}
      <section id="pricing" className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:64px_64px]" />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-[11px] font-bold text-blue-400 uppercase tracking-widest mb-3">Platform Tiers</p>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-white mb-4">Enterprise pricing. Transparent structure.</h2>
            <p className="max-w-2xl mx-auto text-slate-400 text-lg">
              All plans include UK hosting, compliance architecture, and enterprise security. Pricing is based on operational scale and module scope.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {PRICING_PLANS.map((plan) => (
              <div
                key={plan.name}
                className={cn(
                  "rounded-2xl border p-7 flex flex-col",
                  plan.highlight
                    ? "border-blue-500/40 bg-blue-500/8 ring-1 ring-blue-500/20"
                    : "border-white/8 bg-white/[0.03]"
                )}
              >
                {plan.highlight && (
                  <span className="self-start text-[10px] font-bold text-blue-300 uppercase tracking-wider bg-blue-500/20 border border-blue-400/20 px-2.5 py-1 rounded-full mb-4">
                    Most Popular
                  </span>
                )}
                <h3 className="text-lg font-bold text-white mb-1">{plan.name}</h3>
                <div className="flex items-end gap-1 mb-2">
                  <span className="text-4xl font-extrabold text-white">{plan.price}</span>
                  <span className="text-slate-500 text-sm mb-1.5">{plan.period}</span>
                </div>
                <p className="text-sm text-slate-400 mb-6 leading-relaxed">{plan.desc}</p>
                <ul className="space-y-2.5 mb-8 flex-1">
                  {plan.features.map((feat) => (
                    <li key={feat} className="flex items-start gap-2.5 text-sm text-slate-300">
                      <Check className="h-4 w-4 text-emerald-400 shrink-0 mt-0.5" />
                      {feat}
                    </li>
                  ))}
                </ul>
                <a
                  href={plan.ctaHref}
                  className={cn(
                    "w-full text-center py-2.5 px-4 rounded-xl text-sm font-semibold transition-colors",
                    plan.highlight
                      ? "bg-blue-600 hover:bg-blue-500 text-white shadow-lg shadow-blue-600/25"
                      : "border border-white/15 hover:border-white/30 bg-white/5 text-white"
                  )}
                >
                  {plan.cta}
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── DEMO REQUEST FORM ── */}
      <section id="request-demo" className="py-24 bg-slate-900/50 relative overflow-hidden">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[700px] h-[400px] bg-blue-600/8 blur-[100px] rounded-full pointer-events-none" />
        <div className="relative max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-extrabold text-white mb-4">Request a Platform Demonstration</h2>
            <p className="text-slate-400 text-lg leading-relaxed">
              OpsCom platform demonstrations are tailored to your operational context. We show you your sector, your workflows, and your compliance obligations — not a generic product tour.
            </p>
          </div>
          {demoSubmitted ? (
            <div className="rounded-2xl border border-emerald-500/30 bg-emerald-500/10 p-8 text-center">
              <CheckCircle2 className="h-10 w-10 text-emerald-400 mx-auto mb-4" />
              <h3 className="text-lg font-bold text-white mb-2">Demonstration request received.</h3>
              <p className="text-slate-400 text-sm leading-relaxed">
                A member of the OpsCom team will contact you within one working day to arrange a tailored platform walkthrough. You can also reach us directly at{" "}
                <a href="mailto:demos@opscom.co.uk" className="text-blue-400 hover:text-blue-300 transition-colors">demos@opscom.co.uk</a>.
              </p>
            </div>
          ) : (
            <form onSubmit={handleDemoSubmit} className="rounded-2xl border border-white/8 bg-white/[0.03] p-8 space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className="block text-xs font-semibold text-slate-400 mb-1.5">Full Name</label>
                  <input
                    type="text"
                    required
                    value={demoForm.name}
                    onChange={(e) => setDemoForm((f) => ({ ...f, name: e.target.value }))}
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-3.5 py-2.5 text-sm text-white placeholder-slate-600 focus:outline-none focus:border-blue-500/60 focus:ring-1 focus:ring-blue-500/30 transition-colors"
                    placeholder="James Hernandez"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-400 mb-1.5">Organisation</label>
                  <input
                    type="text"
                    required
                    value={demoForm.organisation}
                    onChange={(e) => setDemoForm((f) => ({ ...f, organisation: e.target.value }))}
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-3.5 py-2.5 text-sm text-white placeholder-slate-600 focus:outline-none focus:border-blue-500/60 focus:ring-1 focus:ring-blue-500/30 transition-colors"
                    placeholder="Ockham Emergency Services"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className="block text-xs font-semibold text-slate-400 mb-1.5">Email Address</label>
                  <input
                    type="email"
                    required
                    value={demoForm.email}
                    onChange={(e) => setDemoForm((f) => ({ ...f, email: e.target.value }))}
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-3.5 py-2.5 text-sm text-white placeholder-slate-600 focus:outline-none focus:border-blue-500/60 focus:ring-1 focus:ring-blue-500/30 transition-colors"
                    placeholder="james@example.com"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-400 mb-1.5">Phone Number</label>
                  <input
                    type="text"
                    value={demoForm.phone}
                    onChange={(e) => setDemoForm((f) => ({ ...f, phone: e.target.value }))}
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-3.5 py-2.5 text-sm text-white placeholder-slate-600 focus:outline-none focus:border-blue-500/60 focus:ring-1 focus:ring-blue-500/30 transition-colors"
                    placeholder="+44 7700 000000"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-400 mb-1.5">Operational Sector</label>
                <select
                  required
                  value={demoForm.sector}
                  onChange={(e) => setDemoForm((f) => ({ ...f, sector: e.target.value }))}
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-3.5 py-2.5 text-sm text-white focus:outline-none focus:border-blue-500/60 focus:ring-1 focus:ring-blue-500/30 transition-colors appearance-none"
                >
                  <option value="" disabled className="bg-slate-900">Select your sector</option>
                  <option value="event-medical" className="bg-slate-900">Event Medical</option>
                  <option value="ambulance-service" className="bg-slate-900">Ambulance Service</option>
                  <option value="fire-rescue" className="bg-slate-900">Fire &amp; Rescue</option>
                  <option value="search-rescue" className="bg-slate-900">Search &amp; Rescue</option>
                  <option value="security-operations" className="bg-slate-900">Security Operations</option>
                  <option value="industrial-response" className="bg-slate-900">Industrial Response</option>
                  <option value="other" className="bg-slate-900">Other</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-400 mb-1.5">
                  Additional Context <span className="text-slate-600 font-normal">(optional)</span>
                </label>
                <textarea
                  value={demoForm.message}
                  onChange={(e) => setDemoForm((f) => ({ ...f, message: e.target.value }))}
                  rows={4}
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-3.5 py-2.5 text-sm text-white placeholder-slate-600 focus:outline-none focus:border-blue-500/60 focus:ring-1 focus:ring-blue-500/30 transition-colors resize-none"
                  placeholder="Tell us about your service, operational scale, or specific requirements..."
                />
              </div>
              <button
                type="submit"
                disabled={demoSubmitting}
                className="w-full py-3 px-6 bg-blue-600 hover:bg-blue-500 disabled:opacity-60 disabled:cursor-not-allowed text-white font-semibold rounded-xl transition-colors shadow-lg shadow-blue-600/25 text-sm"
              >
                {demoSubmitting ? "Submitting…" : "Request a Platform Demonstration"}
              </button>
              <p className="text-xs text-slate-600 text-center">
                Responses within one working day · <a href="mailto:demos@opscom.co.uk" className="hover:text-slate-400 transition-colors">demos@opscom.co.uk</a>
              </p>
            </form>
          )}
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer className="bg-slate-950 border-t border-white/8 pt-16 pb-8">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-10 mb-14">
            <div className="md:col-span-1">
              <Link href="/" className="inline-block mb-4">
                <img src="/opscom.png" alt="OpsCom" className="h-8 w-auto" />
              </Link>
              <p className="text-xs text-slate-500 leading-relaxed">
                Integrated operational command, workforce and compliance ecosystem for high-risk environments.
              </p>
            </div>
            <div>
              <h4 className="text-[11px] font-bold text-slate-400 uppercase tracking-widest mb-4">Platform</h4>
              <ul className="space-y-2.5">
                {FOOTER_PLATFORM.map((item) => (
                  <li key={item.label}>
                    <Link href={item.href} className="text-sm text-slate-500 hover:text-slate-300 transition-colors">{item.label}</Link>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h4 className="text-[11px] font-bold text-slate-400 uppercase tracking-widest mb-4">Solutions</h4>
              <ul className="space-y-2.5">
                {FOOTER_SOLUTIONS.map((item) => (
                  <li key={item.label}>
                    <a href={item.href} className="text-sm text-slate-500 hover:text-slate-300 transition-colors">{item.label}</a>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h4 className="text-[11px] font-bold text-slate-400 uppercase tracking-widest mb-4">Ecosystem</h4>
              <ul className="space-y-2.5">
                {FOOTER_ECOSYSTEM.map((item) => (
                  <li key={item.label}>
                    <a href={item.href} className="text-sm text-slate-500 hover:text-slate-300 transition-colors">{item.label}</a>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h4 className="text-[11px] font-bold text-slate-400 uppercase tracking-widest mb-4">Company</h4>
              <ul className="space-y-2.5">
                {FOOTER_COMPANY.map((item) => (
                  <li key={item.label}>
                    <a href={item.href} className="text-sm text-slate-500 hover:text-slate-300 transition-colors">{item.label}</a>
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <div className="border-t border-white/8 pt-6">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <p className="text-xs text-slate-600">© 2026 OpsCom. All rights reserved. UK data hosting. GDPR compliant.</p>
              <div className="flex flex-wrap gap-x-6 gap-y-1.5">
                {PUBLIC_EMAILS.map((e) => (
                  <a key={e.addr} href={`mailto:${e.addr}`} className="text-xs text-slate-600 hover:text-slate-400 transition-colors flex items-center gap-1.5">
                    <Mail className="h-3 w-3 shrink-0" />
                    <span className="hidden sm:inline text-slate-700">{e.label}:</span>
                    {e.addr}
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
      </footer>

    </div>
  );
}
